import type { LinkedItem, Morphs } from '@types'
import { ensureSidebarMorphs } from '@utils/catalog/ensureSidebarMorphs'
import {
  applyKgbrLegacyFondo720To600,
  isKbrRinconeroFamilyCatalog,
} from '@utils/catalog/kbrCatalog'
import { refreshParentGlbBasesFromScene } from '@utils/sceneObjects/linked/ensureIndependentZocaloOnProjectLoad'
import getMorphsFromGLB from '../../../store/getMorphsFromGLB'

type UnidadHeader = {
  ancho?: number | null
  alto?: number | null
  fondo?: number | null
  morphs?: unknown
  regleta?: number | null
  regletaMinimo?: number | null
  regletaMaximo?: number | null
}

export type KbrFamilyRebaseResult = {
  widthChanged: boolean
  heightChanged: boolean
  thicknessChanged: boolean
}

function mm(value: unknown): number {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : 0
}

function morphCatalogMm(morphs: Morphs[] | undefined, name: string): number {
  const m = morphs?.find((morph) => morph.name === name)
  return mm(m?._default) || mm(m?.default)
}

/**
 * Catálogo nuevo + delta que el usuario tuviera sobre el catálogo anterior.
 * Sin cambios del usuario (live ≈ catálogo viejo) → catálogo nuevo tal cual.
 */
function rebaseAxis(live: number, prevCatalog: number, nextCatalog: number): number {
  if (!(nextCatalog > 0)) return live
  if (!(live > 0) || !(prevCatalog > 0)) return nextCatalog
  return Math.round(nextCatalog + (live - prevCatalog))
}

/**
 * KBR ↔ KGBR (toggle Gola del rinconero): la API no trae morphs, así que el
 * cambio de unidad no refrescaba ancho/alto/fondo y la unidad nueva heredaba el
 * contrato del catálogo anterior. KGBR alto 800 = solo cuerpo (+ lift 100 de la
 * tira); KBR alto 900 = mueble con hueco de zócalo. Con 800 en KBR el cuerpo se
 * queda en 700 y la encimera baja 100; al volver a KGBR las bases free-dim
 * (bbox/catálogo) seguían siendo las del KBR y la encimera flotaba.
 *
 * Reescribe dims + freeDimensionCatalogMm + morphs sintéticos (como drawItem).
 * Las bases del GLB (freeDimensionGlbBaseMm) se refrescan aparte cuando el GLB
 * nuevo ya está cargado. Devuelve null si no aplica.
 */
export function rebaseKbrFamilyDimsAfterUnitChange(
  item: LinkedItem,
  unidad: UnidadHeader,
): KbrFamilyRebaseResult | null {
  if (!isKbrRinconeroFamilyCatalog(item.catalogID)) return null
  // Con morphs de API el flujo normal ya reconstruye morphs/dims.
  if (Array.isArray(unidad.morphs) && unidad.morphs.length > 0) return null

  const next = { x: mm(unidad.ancho), y: mm(unidad.alto), z: mm(unidad.fondo) }
  if (!(next.x > 0) || !(next.y > 0)) return null

  const prevMorphs = Array.isArray(item.morphs) ? (item.morphs as Morphs[]) : undefined
  const prev = {
    x: mm(item.freeDimensionCatalogMm?.x) || morphCatalogMm(prevMorphs, 'Ancho'),
    y: mm(item.freeDimensionCatalogMm?.y) || morphCatalogMm(prevMorphs, 'Alto'),
    z: mm(item.freeDimensionCatalogMm?.z) || morphCatalogMm(prevMorphs, 'Fondo'),
  }

  const width = rebaseAxis(mm(item.width), prev.x, next.x)
  const height = rebaseAxis(mm(item.height), prev.y, next.y)
  const thickness = next.z > 0 ? rebaseAxis(mm(item.thickness), prev.z, next.z) : mm(item.thickness)

  const result: KbrFamilyRebaseResult = {
    widthChanged: width !== mm(item.width),
    heightChanged: height !== mm(item.height),
    thicknessChanged: thickness !== mm(item.thickness),
  }

  item.width = width
  item.height = height
  if (thickness > 0) item.thickness = thickness
  item.freeDimensionCatalogMm = {
    x: next.x,
    y: next.y,
    z: next.z > 0 ? next.z : mm(item.thickness),
  }

  // Regleta: conservar la del usuario dentro del rango de la unidad nueva
  // (regletaMinimo/Maximo ya vienen actualizados desde la API).
  const prevRegleta = prevMorphs?.find((m) => m.name === 'Regleta')
  const preservedRegleta = mm(prevRegleta?.default) || mm(item.regleta)
  const regletaMin = Number(item.regletaMinimo)
  const regletaMax = Number(item.regletaMaximo)
  if (preservedRegleta > 0 && Number.isFinite(regletaMin) && Number.isFinite(regletaMax)) {
    item.regleta = Math.max(regletaMin, Math.min(regletaMax, preservedRegleta))
  }

  // Morphs sintéticos + Regleta como drawItem: _default = catálogo, default = layout.
  const fromApi = getMorphsFromGLB(unidad as Parameters<typeof getMorphsFromGLB>[0])
  item.morphs = ensureSidebarMorphs({ ...item, morphs: fromApi }).map((m) => {
    if (m.name === 'Ancho') return { ...m, _default: next.x, default: item.width }
    if (m.name === 'Alto') return { ...m, _default: next.y, default: item.height }
    if (m.name === 'Fondo') {
      return { ...m, _default: item.freeDimensionCatalogMm!.z, default: item.thickness }
    }
    return m
  }) as Morphs[]
  // Como drawItem para KBR* (LinkedItem no declara hasMorphs).
  ;(item as { hasMorphs?: boolean }).hasMorphs = true

  return result
}

/**
 * Con el GLB nuevo ya cargado: bbox/catálogo/scaleBase del asset actual (como al
 * abrir proyecto) y el fix KGBR fondo 720 → 600 de la BD.
 */
export function refreshKbrFamilyGlbBases(
  item: LinkedItem,
  sceneEntry: { glb: unknown; backGLBInfo: unknown },
): void {
  refreshParentGlbBasesFromScene(
    item,
    sceneEntry as Parameters<typeof refreshParentGlbBasesFromScene>[1],
  )
  applyKgbrLegacyFondo720To600(item)
}
